import { ProjConstants, storageConst, Tabs } from '../constants/projConsts';

type StorageData = {
    [K in keyof typeof storageConst]: K extends 'ASSIST_INTERRUPT_DIALOG_LIST' | 'MYBOT_INTERRUPT_DIALOG_LIST' ? any[] :
      K extends 'PROACTIVE_MODE' | 'AUTOSCROLL' ? boolean :
      K extends 'ACTIVE_TAB' | 'LANGUAGE' ? string :
      K extends 'INTERNAL_TRANSFER' ? any : never;
  }
  

interface WidgetSettings {
  isProactiveEnabled?: boolean;
  isAutoScrollEnabled?: boolean;
  languageSettings?: { language: string };
}

export default class LocalStorageService {
    private readonly hostIns: any;
    private readonly rootService: any;
    
    constructor(hostIns: any) {
        this.hostIns = hostIns;
        this.rootService = hostIns.rootService;
    }

    private get conversationId(): string {
        return this.rootService?.connectionDetails?.conversationId;
    }

    private getStorageItem<T>(key: string, defaultValue: T): T {
        try {
            const item = localStorage.getItem(key);
            return item ? JSON.parse(item) : defaultValue;
        } catch {
            return defaultValue;
        }
    }

    private setStorageItem(key: string, value: any): void {
        try {
            localStorage.setItem(key, JSON.stringify(value));
        } catch (error) {
            console.warn('Failed to set localStorage item:', error);
        }
    }

    getConvObjFromLS(): StorageData {
        return this.getStorageItem(this.conversationId, {});
    }

    deleteLocalStorageState(convId: string): void {
        const data = this.getConvObjFromLS();
        if (Object.keys(data).length > 0) {
            localStorage.removeItem(convId);
        }
    }

    initializeLocalStorageData(widgetSettings: WidgetSettings) {
        const existingData = this.getConvObjFromLS();
        let widgetLanguage:any = widgetSettings?.languageSettings?.language
        if(widgetLanguage === 'interactiveLang'){
            widgetLanguage = this.hostIns?.config?.connectionDetails?.interactiveLanguage
        }
        
        if (Object.keys(existingData).length === 0 || (Object.keys(existingData).length === 1 && existingData.hasOwnProperty(storageConst.INTERNAL_TRANSFER))) {
            const theme = this.getThemeLocal();
            this.setThemeLocal(theme);

            const defaultData = {
                [storageConst.ASSIST_INTERRUPT_DIALOG_LIST]: [],
                [storageConst.MYBOT_INTERRUPT_DIALOG_LIST]: [],
                [storageConst.PROACTIVE_MODE]: widgetSettings?.isProactiveEnabled ?? true,
                // [storageConst.ACTIVE_TAB]: Tabs.ASSIST,
                [storageConst.AUTOSCROLL]: widgetSettings?.isAutoScrollEnabled ?? true,
                [storageConst.LANGUAGE]:  widgetLanguage || storageConst.ENGLISH,
                [storageConst.FINAL_SUMMARY_GENERATED]: false
            };
            if (this.shouldAddInternalTransfer()) {
                
                defaultData[storageConst.INTERNAL_TRANSFER] = this.rootService.internalTransferData;
            }

            this.updateRootServiceState(defaultData);
            this.setStorageItem(this.conversationId, defaultData);
        } else {
            this.updateRootServiceState(existingData);
        }
    }

    private shouldAddInternalTransfer(): boolean {
        return !!(
            this.rootService.internalTransfer && 
            Object.keys(this.rootService.internalTransferData || {}).length > 0 && 
            this.conversationId === this.rootService.internalTransferData?.conversationId
        );
    }

    private updateRootServiceState(data: any): void {
        this.rootService.autoScrollStatus = data[storageConst.AUTOSCROLL];
        this.rootService.proactiveMode = data[storageConst.PROACTIVE_MODE];
        this.rootService.activeTab = data[storageConst.ACTIVE_TAB];
    }

    // Simplified setters using a generic method
    private updateStorageValue<T>(key: keyof StorageData, value: T): void {
        const data : any = this.getConvObjFromLS();
        if (data) {
            data[key] = value;
            this.setStorageItem(this.conversationId, data);
        }
    }

    setInterruptDialogList = (dialogs: any[]) => 
        this.updateStorageValue(storageConst.ASSIST_INTERRUPT_DIALOG_LIST, dialogs);
    
    setMyBotInterruptDialogList = (dialogs: any[]) => 
        this.updateStorageValue(storageConst.MYBOT_INTERRUPT_DIALOG_LIST, dialogs);
    
    setCustomInterruptDialogList = (dialogs: any[]) => 
        this.updateStorageValue(storageConst.CUSTOM_INTERRUPT_DIALOG_LIST, dialogs);
    
    setProactiveMode = (mode: boolean) => 
        this.updateStorageValue(storageConst.PROACTIVE_MODE, mode);
    
    setActiveTab = (tab: string) => 
        this.updateStorageValue(storageConst.ACTIVE_TAB, tab);
    
    setAutoScroll = (autoScroll: boolean) => 
        this.updateStorageValue(storageConst.AUTOSCROLL, autoScroll);
    
    setLanguageInfo = (lang: string) => 
        this.updateStorageValue(storageConst.LANGUAGE, lang);
    
    setInternalTransferInfo = (data: any) => 
        this.updateStorageValue(storageConst.INTERNAL_TRANSFER, data);

    setThemeLocal(theme = storageConst.LIGHT): void {
        localStorage.setItem(storageConst.THEME, theme);
    }
    
    setFinalSummaryGenerated = (isGenerated: boolean) => 
        this.updateStorageValue(storageConst.FINAL_SUMMARY_GENERATED, isGenerated);

    // Simplified getters using a generic method
    private getStorageValue<T>(key: keyof StorageData, defaultValue: T): T {
        const data : any = this.getConvObjFromLS();
        return data ? (data[key] as T) ?? defaultValue : defaultValue;
    }

    getInterruptDialogList = () => 
        this.getStorageValue(storageConst.ASSIST_INTERRUPT_DIALOG_LIST, []);
    
    getMyBotInterruptDialogList = () => 
        this.getStorageValue(storageConst.MYBOT_INTERRUPT_DIALOG_LIST, []);
    
    getProactiveMode = () => 
        this.getStorageValue(storageConst.PROACTIVE_MODE, true);
    
    getActiveTab = () => 
        this.getStorageValue(storageConst.ACTIVE_TAB, Tabs.ASSIST);
    
    getAutoScroll = () => 
        this.getStorageValue(storageConst.AUTOSCROLL, true);
    
    getLanguage = () => 
        this.getStorageValue(storageConst.LANGUAGE, null);
    
    getInternalTransferInfo = () => 
        this.getStorageValue(storageConst.INTERNAL_TRANSFER, {});

    getThemeLocal = (): string => 
        localStorage.getItem(storageConst.THEME) || storageConst.LIGHT;

    getFinalSummaryGenerated = () => 
        this.getStorageValue(storageConst.FINAL_SUMMARY_GENERATED, false);
}
